import { ErrorMessage, Field, Form, Formik, type FieldProps } from 'formik';
import { useRef, useState } from 'react';

import {
  REMINDER_TEMPLATE_STATUS,
  REMINDER_TEMPLATE_CATEGORY,
  REMINDER_TEMPLATE_CONTENT_TYPES,
  type TReminderTemplate,
} from '@src/shared/types/reminder-template';
import reminderTemplateApi from '@src/features/reminders/services/reminder-template-api';
import {
  createReminderTemplateInitialValues,
  reminderTemplateFormSchema,
  type TReminderTemplateFormValues,
} from '@shared/features/reminders/dialogs/create-or-edit-reminder-template/schema';
import { Textfield } from '@src/shared/fields/text-field';
import { SelectField } from '@src/shared/fields/select-field';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { Label } from '@src/design-system/label';
import { Languages } from '@src/shared/constants/languages';
import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from '@src/design-system/dialog';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { Textarea, TextareaInput } from '@src/design-system/textarea';
import { syncVariablesFromContent } from '@shared/features/reminders/helpers/sync-variables-from-content';
import { TiptapEditor } from '@src/shared/components/reusables/tiptap-editor';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';
import { AddVariableDropdown } from '../../add-variable-dropdown';
import { AttachmentsField } from '../../attachments-field';
import { getChannelSvg } from '@src/shared/features/reminders/helpers/get-channel-svg';
import { LivePreview } from '@src/shared/features/reminders/live-preview';
import { useTemplateChannelContentDrafts } from '@shared/features/reminders/helpers/use-template-channel-content-drafts';
import { cn } from '@src/design-system/helpers';

function contentMaxLengthForChannel(channel: string) {
  if (channel === REMINDER_CHANNELS.sms) return 480;
  if (channel === REMINDER_CHANNELS.whatsapp) return 1024;
  return 4096;
}

export const CreateOrEditReminderTemplate = ({
  reminderTemplate,
}: {
  reminderTemplate?: TReminderTemplate;
}) => {
  const { formikRef } = useDialogContext();
  const [submitMode, setSubmitMode] = useState<'confirm' | 'draft'>('confirm');

  const { mutate: addReminderTemplate, isPending: isCreating } =
    reminderTemplateApi.useCreate();
  const { mutate: editReminderTemplate, isPending: isUpdating } =
    reminderTemplateApi.useUpdate();

  const closeRef = useRef<HTMLButtonElement>(null);
  const initialValues = {
    ...createReminderTemplateInitialValues(reminderTemplate),
    category: reminderTemplate?.category ?? REMINDER_TEMPLATE_CATEGORY.utility,
  };

  const { resolveContentForChannelChange } = useTemplateChannelContentDrafts(
    reminderTemplate?.id,
    initialValues.channel,
    initialValues.content
  );

  const handleSubmit = (values: TReminderTemplateFormValues) => {
    const attachmentIds =
      values.attachments?.map((attachment) => attachment.id) ?? [];

    const content_type =
      values.channel === REMINDER_CHANNELS.sms
        ? REMINDER_TEMPLATE_CONTENT_TYPES.text
        : attachmentIds.length
          ? REMINDER_TEMPLATE_CONTENT_TYPES.media
          : REMINDER_TEMPLATE_CONTENT_TYPES.text;

    const data = {
      ...values,
      attachments: attachmentIds,
      content_type,
      category: values.category || REMINDER_TEMPLATE_CATEGORY.utility,
      status:
        submitMode === 'draft'
          ? REMINDER_TEMPLATE_STATUS.saved
          : reminderTemplate?.status,
    };

    const onSuccess = () => {
      closeRef.current?.click();
    };

    if (reminderTemplate?.id) {
      return editReminderTemplate(
        {
          id: reminderTemplate?.id,
          ...data,
        },
        { onSuccess }
      );
    } else {
      return addReminderTemplate(data, { onSuccess });
    }
  };

  return (
    <>
      <DialogHeader className='px-4 pt-5 pb-3'>
        <DialogTitle className='text-base mb-2'>
          {reminderTemplate ? 'Edit Template' : 'Add New Template'}
        </DialogTitle>
        <p className='text-sm text-gray-600'>
          Saved Templates can be used in reminders and campaigns{' '}
        </p>
      </DialogHeader>
      <DialogBody className='flex w-full max-w-full flex-col overflow-hidden p-0'>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={reminderTemplateFormSchema}
          onSubmit={handleSubmit}
        >
          {({ values, submitForm, setFieldValue }) => {
            const handleChannelChange = (channel: string) => {
              if (values.channel === channel) return;

              let nextContent = resolveContentForChannelChange(
                values.channel,
                channel,
                values.content ?? ''
              );
              const maxLen = contentMaxLengthForChannel(channel);
              if (nextContent.length > maxLen) {
                nextContent = nextContent.slice(0, maxLen);
              }

              setFieldValue('channel', channel);
              setFieldValue('content', nextContent);
              syncVariablesFromContent(nextContent, setFieldValue);
            };

            return (
              <Form className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'>
                <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden md:flex-row md:gap-6'>
                  <div className='min-h-0 p-4 min-w-0 flex-1 basis-0 space-y-4 overflow-x-hidden overflow-y-auto pr-1 md:max-w-[50%]'>
                    <div>
                      <Textfield
                        label='Template Name'
                        name='name'
                        placeholder='Enter Reminder Name'
                      />
                    </div>
                    <div>
                      <Label>Language</Label>
                      <SelectField
                        selectName='language'
                        selectPlaceholder='Select language'
                        name='language'
                        options={Languages}
                      />
                    </div>
                    <div>
                      <Label>Channel</Label>
                      <div className='mt-2 grid grid-cols-3 gap-3'>
                        {[
                          REMINDER_CHANNELS.whatsapp,
                          REMINDER_CHANNELS.sms,
                          REMINDER_CHANNELS.email,
                        ].map((channel) => (
                          <button
                            key={channel}
                            type='button'
                            onClick={() => handleChannelChange(channel)}
                            className={cn(
                              'h-19 cursor-pointer rounded-lg border px-3 flex items-center justify-center gap-2 text-sm',
                              values.channel === channel
                                ? 'border-primary-600 bg-primary-50'
                                : 'border-primary-200 bg-white'
                            )}
                          >
                            <img
                              src={getChannelSvg(channel)}
                              alt={channel}
                              className='w-5! h-5!'
                            />

                            {channel === REMINDER_CHANNELS.sms
                              ? 'SMS'
                              : formatTextForDisplay(channel)}
                          </button>
                        ))}
                      </div>
                      <ErrorMessage
                        name='channel'
                        component={CustomErrorMessage}
                      />
                    </div>
                    <div className='my-2 min-w-0 w-full max-w-full'>
                      <div className='mb-2 flex w-full min-w-0 items-center justify-between gap-2'>
                        <Label className='mb-0 text-sm font-medium text-gray-800'>
                          Message Content:
                        </Label>
                        <AddVariableDropdown />
                      </div>

                      <div className='min-w-0 w-full max-w-full overflow-hidden'>
                        {values.channel === REMINDER_CHANNELS.email ? (
                          <TiptapEditor
                            name='content'
                            value={values.content}
                            className='w-full min-w-0 max-w-full'
                            contentClassName='!max-w-full [&_.ProseMirror]:break-words [&_.ProseMirror]:whitespace-pre-wrap'
                            onChange={(val) => {
                              setFieldValue('content', val);
                              syncVariablesFromContent(val, setFieldValue);
                            }}
                          />
                        ) : (
                          <Textarea
                            key={values.channel}
                            maxLength={contentMaxLengthForChannel(
                              values.channel
                            )}
                          >
                            <Field name='content'>
                              {({ field, meta }: FieldProps<string>) => {
                                const maxLen = contentMaxLengthForChannel(
                                  values.channel
                                );
                                const len =
                                  (field.value as string)?.length ?? 0;
                                return (
                                  <>
                                    <TextareaInput
                                      placeholder='enter message here...'
                                      {...field}
                                      className='w-full min-w-0 max-w-full resize-y [field-sizing:fixed]'
                                      hasError={!!(meta.touched && meta.error)}
                                      onChange={(e) => {
                                        field.onChange(e);
                                        syncVariablesFromContent(
                                          e.target.value,
                                          setFieldValue
                                        );
                                      }}
                                    />

                                    {values.channel ===
                                    REMINDER_CHANNELS.sms ? (
                                      <div className='mt-2 space-y-0.5 text-xs leading-relaxed text-gray-600'>
                                        <p>
                                          Standard SMS is limited to{' '}
                                          <span className='font-medium'>
                                            160 characters
                                          </span>{' '}
                                          per segment.
                                        </p>
                                        <p>
                                          Exceeding this limit or using emojis
                                          will increase the number of segments
                                          billed.
                                        </p>
                                      </div>
                                    ) : (
                                      <p className='mt-1.5 text-right text-xs text-gray-500'>
                                        {len} / {maxLen}
                                      </p>
                                    )}
                                  </>
                                );
                              }}
                            </Field>
                          </Textarea>
                        )}
                      </div>
                      <ErrorMessage
                        name='content'
                        component={CustomErrorMessage}
                      />
                    </div>
                    <div>
                      <AttachmentsField />
                    </div>
                  </div>

                  <div className='flex bg-primary-50 min-h-0 min-w-0 w-full shrink-0 flex-col items-center overflow-y-auto border-t border-primary-100 pt-4 md:w-1/2 md:max-w-[50%] md:border-l md:border-t-0 md:pl-6 md:pt-0'>
                    <LivePreview
                      attachments={values.attachments ?? []}
                      channel={values.channel}
                      className='min-h-0 w-full flex-1'
                      content={values.content}
                    />
                  </div>
                </div>

                <div className='mt-2 p-4 grid w-full min-w-0 shrink-0 grid-cols-[1fr_1fr] gap-2 border-t border-primary-100 bg-white pt-4 md:w-1/2'>
                  <Button
                    isLoading={isCreating || isUpdating}
                    className='min-w-0 w-full'
                    onClick={() => {
                      setSubmitMode('confirm');
                      submitForm();
                    }}
                  >
                    Confirm
                  </Button>
                  <DialogClose
                    ref={closeRef}
                    asChild
                  >
                    <Button
                      className='min-w-0 w-full'
                      variant='primaryOutline'
                    >
                      Cancel
                    </Button>
                  </DialogClose>

                  {/* <Button
                    variant='primaryOutline'
                    className='min-w-0 w-full'
                    onClick={() => {
                      setSubmitMode('draft');
                      submitForm();
                    }}
                  >
                    Save as Draft
                  </Button> */}
                </div>
              </Form>
            );
          }}
        </Formik>
      </DialogBody>
    </>
  );
};
